import { Fragment, useEffect, useRef, useState } from 'react';

import { type V3PresentationContext } from '@apps/web/features/phoebe_chat_v3/model/presentation_context';
import { fetchAttachmentBlob } from '@apps/web/features/phoebe_chat_v3/ui/chat_attachment_fetch';
import { triggerBlobDownload } from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';
import { ApiError } from '@libraries/typescript/api_client/error';

// Blob-based download: fetch the file through the api client so `X-Mode`
// (and every other auth header the api client injects) rides along, then
// hand the bytes to the shared `triggerBlobDownload` from
// `apps/web/lib/tabular_export` — that helper owns the Arc/Chromium-safe
// dispatch (a real MouseEvent instead of `.click()`) AND the delayed
// `URL.revokeObjectURL` so Chromium/Arc actually get to fetch the blob
// before it's freed. A plain `<a href download>` cannot set headers, so
// navigating to the download URL directly would omit `X-Mode` and the
// endpoint would reject the request with 422 (see
// attachments.py::require_mode_context_from_header).
export function DownloadButton({
    context,
    runId,
    attachmentId,
    filename,
    className,
    children,
}: {
    readonly context: V3PresentationContext;
    readonly runId: string;
    readonly attachmentId: string;
    readonly filename: string;
    readonly className?: string;
    readonly children: React.ReactNode;
}) {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const controllerRef = useRef<AbortController | null>(null);
    const { organizationId, mode } = context;
    useEffect(() => {
        return () => {
            controllerRef.current?.abort();
        };
    }, []);
    return (
        <Fragment>
            <button
                type="button"
                disabled={isDownloading}
                onClick={async () => {
                    setDownloadError(null);
                    setIsDownloading(true);
                    controllerRef.current?.abort();
                    const controller = new AbortController();
                    controllerRef.current = controller;
                    try {
                        const blob = await fetchAttachmentBlob({
                            organizationId,
                            runId,
                            attachmentId,
                            download: true,
                            mode,
                            signal: controller.signal,
                        });
                        triggerBlobDownload(blob, filename);
                    } catch (error) {
                        if (!controller.signal.aborted) {
                            const status =
                                error !== null &&
                                typeof error === 'object' &&
                                'status' in error &&
                                typeof error.status === 'number'
                                    ? error.status
                                    : null;
                            const message =
                                status !== null
                                    ? `Download failed with ${status}`
                                    : error instanceof Error
                                      ? error.message
                                      : 'Download failed';
                            const requestId =
                                error instanceof ApiError
                                    ? error.requestId
                                    : null;
                            setDownloadError(
                                requestId
                                    ? `${message} (support id: ${requestId})`
                                    : message,
                            );
                        }
                    } finally {
                        if (controllerRef.current === controller) {
                            controllerRef.current = null;
                            setIsDownloading(false);
                        }
                    }
                }}
                aria-label={
                    downloadError
                        ? `Download failed: ${downloadError}`
                        : undefined
                }
                className={cn(
                    'inline-flex items-center gap-1 text-[13px] leading-4 font-medium text-[#5533EE] hover:text-[#4629C7] disabled:cursor-progress disabled:opacity-70',
                    className,
                )}
            >
                {children}
            </button>
            {downloadError ? <span role="alert">{downloadError}</span> : null}
        </Fragment>
    );
}
